import axios from 'axios';
import Session from 'supertokens-web-js/recipe/session';
import { addMetadata, leaveBreadcrumb } from '@/config/bugsnagHelpers';
import { IDLE_SESSION_CONFIG } from '@/config/idleSessionConfig';

const apiDomain =
  process.env.NODE_ENV !== 'development'
    ? process.env.NEXT_PUBLIC_API_DOMAIN
    : 'http://localhost:3001';

const client = axios.create({
  baseURL: apiDomain,
  withCredentials: true,
});

/**
 * Event emitter for activity tracking.
 * IdleDetectionProvider listens to this event.
 */
export const emitActivity = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('ltdan-api-activity', {
        detail: { timestamp: Date.now() },
      }),
    );
  }
};

/**
 * Helper function to handle idle timeout logout and redirect.
 * Centralized to avoid duplication between 401 and 403 handlers.
 */
const handleIdleTimeoutLogout = async () => {
  try {
    await Session.signOut();
  } catch {
    // ignore signOut errors and still redirect
  }

  // Clear activity storage
  try {
    localStorage.removeItem(IDLE_SESSION_CONFIG.STORAGE_KEY);
  } catch {}

  if (typeof window !== 'undefined') {
    window.location.href = '/';
  }
};

/**
 * Auth endpoints that should NOT include existing session tokens.
 * These endpoints create new sessions, so stale tokens can cause issues.
 */
const AUTH_ENDPOINTS_NO_SESSION = [
  '/api/auth/signup',
  '/api/auth/signin',
  '/api/auth/user/password/reset',
];

/**
 * Request interceptor - runs before each request.
 */
client.interceptors.request.use(
  (config) => {
    // Record activity for all requests
    emitActivity();

    // Remove Authorization header for auth endpoints to prevent stale session interference
    const url = config.url || '';
    if (AUTH_ENDPOINTS_NO_SESSION.some((endpoint) => url.includes(endpoint))) {
      delete config.headers['Authorization'];
      delete config.headers['authorization'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/**
 * Response interceptor - handles responses and errors.
 */
client.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    try {
      const status = error?.response?.status;
      const data = error?.response?.data;
      const config = error?.config;

      const isIdleTimeoutError = status === 401 && data?.code === 'IDLE_TIMEOUT';

      // Don't report idle timeout errors to Bugsnag
      if (!isIdleTimeoutError) {
        try {
          leaveBreadcrumb('API error', {
            url: config?.url,
            method: config?.method,
            status,
          });
          addMetadata('lastApiError', {
            url: config?.url,
            method: config?.method,
            status,
            data,
          });
        } catch {}
      }

      if (isIdleTimeoutError) {
        await handleIdleTimeoutLogout();
        return Promise.reject(error);
      }

      if (
        status === 403 &&
        data &&
        data.message === 'invalid claim' &&
        Array.isArray(data.claimValidationErrors)
      ) {
        const hasIdleTimeoutError = data.claimValidationErrors.some(
          (err: any) => err?.id === IDLE_SESSION_CONFIG.ERROR_CODE,
        );

        if (hasIdleTimeoutError) {
          await handleIdleTimeoutLogout();
          return Promise.reject(error);
        }

        const hasLatestSessionError = data.claimValidationErrors.some(
          (err: any) => err?.id === 'isLatestSession',
        );

        if (hasLatestSessionError) {
          try {
            await Session.signOut();
          } catch {
            // ignore signOut errors and still redirect
          }

          if (typeof window !== 'undefined') {
            window.location.href = '/';
          }
          return Promise.reject(error);
        }
      }
    } catch {
      // fall through to default error handling
    }

    return Promise.reject(error);
  },
);

export default client;
